import { httpDelete, httpGet, httpPost, httpPut } from "../http";
import { femaCapture } from "../../fema/capture";
import { uuEvidenceEndpoints } from "../endpoints";
import { ApiResponse, UcEvidencePeriod, UcEvidenceUpsertRequest } from "../types";

const ERROR_CONTEXT = {
  feature: "uu-evidence",
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isUcEvidencePeriod = (value: unknown): value is UcEvidencePeriod => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.customerId === "number" &&
    (value.month === undefined || typeof value.month === "string") &&
    (value.year === undefined || typeof value.year === "string") &&
    typeof value.entrySource === "string"
  );
};

const isApiResponsePayload = (value: unknown): value is ApiResponse<unknown> =>
  isRecord(value) && typeof value.success === "boolean" && "timestamp" in value;

const normalizeEvidencePayload = (
  payload: unknown
): UcEvidencePeriod | UcEvidencePeriod[] | undefined => {
  if (Array.isArray(payload)) {
    const records = payload.filter(isUcEvidencePeriod);
    return records.length > 0 ? records : [];
  }

  if (isUcEvidencePeriod(payload)) {
    return payload;
  }

  if (isApiResponsePayload(payload)) {
    const { data } = payload;
    if (Array.isArray(data)) {
      const records = data.filter(isUcEvidencePeriod);
      return records.length > 0 ? records : [];
    }
    if (isUcEvidencePeriod(data)) {
      return data;
    }
  }

  return undefined;
};

const ensureSingleRecord = (payload: unknown): UcEvidencePeriod => {
  const normalized = normalizeEvidencePayload(payload);
  if (Array.isArray(normalized)) {
    if (normalized.length > 0) {
      return normalized[0];
    }
    throw new Error("UC evidence response returned an empty collection.");
  }
  if (normalized) {
    return normalized;
  }
  throw new Error("Unable to parse UC evidence response.");
};

export class UuEvidenceService {
  static async getCustomerEvidencePeriods(
    customerId: string | number
  ): Promise<UcEvidencePeriod[]> {
    try {
      const response = await httpGet<ApiResponse<UcEvidencePeriod[]>>(
        uuEvidenceEndpoints.getCustomerEvidencePeriods(customerId)
      );

      const normalized = normalizeEvidencePayload(response.data);
      if (Array.isArray(normalized)) {
        return normalized;
      }
      return normalized ? [normalized] : [];
    } catch (error) {
      femaCapture("uu_evidence_fetch_error", error, {
        category: "api",
        severity: "error",
        context: { ...ERROR_CONTEXT, customerId },
        skipSanitization: false,
        user: {},
      });
      throw error;
    }
  }

  static async createEvidencePeriod(
    payload: UcEvidenceUpsertRequest
  ): Promise<UcEvidencePeriod> {
    try {
      const response = await httpPost<ApiResponse<UcEvidencePeriod>>(
        uuEvidenceEndpoints.createEvidencePeriod(),
        payload
      );

      return ensureSingleRecord(response.data);
    } catch (error) {
      femaCapture("uu_evidence_create_error", error, {
        category: "api",
        severity: "error",
        context: { ...ERROR_CONTEXT, entrySource: payload.entrySource },
        skipSanitization: false,
        user: {},
      });
      throw error;
    }
  }

  static async updateEvidencePeriod(
    payload: UcEvidenceUpsertRequest & { id: number }
  ): Promise<UcEvidencePeriod> {
    try {
      const response = await httpPut<ApiResponse<UcEvidencePeriod>>(
        uuEvidenceEndpoints.updateEvidencePeriod(),
        payload
      );

      return ensureSingleRecord(response.data);
    } catch (error) {
      femaCapture("uu_evidence_update_error", error, {
        category: "api",
        severity: "error",
        context: { ...ERROR_CONTEXT, entrySource: payload.entrySource, id: payload.id },
        skipSanitization: false,
        user: {},
      });
      throw error;
    }
  }

  static async deleteEvidencePeriod(
    id: number,
    customerId: string | number
  ): Promise<void> {
    try {
      await httpDelete(
        uuEvidenceEndpoints.deleteEvidencePeriod(id, customerId)
      );
    } catch (error) {
      femaCapture("uu_evidence_delete_error", error, {
        category: "api",
        severity: "error",
        context: { ...ERROR_CONTEXT, id, customerId },
        skipSanitization: false,
        user: {},
      });
      throw error;
    }
  }
}
